import { Localized } from '../schema/localized'

/**
 * The venue's menu as the page draws it: categories, and in each the dishes
 * with a name, a line of description, a price and a photo.
 *
 * A snapshot handed to the renderer, never stored in the document. Prices
 * change every week and a page published in March must show May's prices:
 * the prerender resolves the menu against `/public/restaurants/{id}/dishes`
 * and `/categories` at publish time, and the editor's preview against the
 * same lists through the RMS. The document keeps only the fact that the menu
 * is shown, and how.
 */
export interface MenuDish {
  id: string
  name: Localized<string>
  description?: Localized<string>
  /** In the venue's minor units, as every amount crosses the API. */
  price: number
  imageUrl?: string
}

export interface MenuCategory {
  id: string
  name: Localized<string>
  dishes: MenuDish[]
}

export interface MenuSnapshot {
  /** ISO 4217, the venue's own; it decides the symbol and the minor unit. */
  currency: string
  categories: MenuCategory[]
}

/**
 * 2800 -> "28,00 zł" for PLN in Polish, "€28.00" for EUR in English.
 *
 * The renderer's own formatter and not `shared/utils/price.ts`: that one reads
 * the currency off `localStorage`, which the prerender does not have, and this
 * page is drawn for the visitor's language rather than the RMS's locale. The
 * minor-unit exponent comes from Intl, so 100 is never assumed (CLAUDE.md §7).
 */
export function formatMinorUnits(
  minorUnits: number,
  currency: string,
  language: string
): string {
  let digits = 2
  try {
    digits =
      new Intl.NumberFormat(language, { style: 'currency', currency })
        .resolvedOptions().maximumFractionDigits ?? 2
  } catch {
    digits = 2
  }
  const amount = minorUnits / 10 ** digits
  try {
    return amount.toLocaleString(language, { style: 'currency', currency })
  } catch {
    // An amount without its symbol beats no amount at all.
    return `${amount.toFixed(digits)} ${currency}`
  }
}
